import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { DigitalAsset } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { slackHook } from "src/utils/snug-slack-hooks";
import { useMounted } from "../use-mounted";

// Action Types
type DigitalAssetStoreAction =
  | { type: "SET_DIGITAL_ASSET"; payload: DigitalAsset[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_DIGITAL_ASSET"; payload: DigitalAsset }
  | { type: "DELETE_DIGITAL_ASSET"; payload: string }
  | { type: "SET_DIGITAL_ASSET_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_DIGITAL_ASSET_SAVING_NEW"; payload: boolean };

// Reducer Function
const digitalAssetStoreReducer = (
  state: DigitalAssetStoreState,
  action: DigitalAssetStoreAction
): DigitalAssetStoreState => {
  switch (action.type) {
    case "SET_DIGITAL_ASSET":
      return {
        ...state,
        digitalAsset: action.payload,
        digitalAssetLoading: false,
      };

    case "SET_LOADING":
      return { ...state, digitalAssetLoading: action.payload };

    case "ADD_OR_UPDATE_DIGITAL_ASSET":
      const index = state.digitalAsset.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedDigitalAsset = [...state.digitalAsset];
      if (index > -1) {
        updatedDigitalAsset[index] = action.payload;
      } else {
        updatedDigitalAsset.push(action.payload);
      }
      return { ...state, digitalAsset: updatedDigitalAsset };

    case "DELETE_DIGITAL_ASSET":
      return {
        ...state,
        digitalAsset: state.digitalAsset.filter((l) => l.id !== action.payload),
      };
    case "SET_DIGITAL_ASSET_FILES_TO_UPLOAD":
      return { ...state, digitalAssetFilestoUpload: action.payload };
    case "SET_DIGITAL_ASSET_SAVING_NEW":
      return { ...state, digitalAssetSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface DigitalAssetStoreState {
  digitalAsset: DigitalAsset[];
  digitalAssetLoading: boolean;
  digitalAssetSavingNew: boolean;
  digitalAssetFilestoUpload: File[];
}

// Hook
export const useDigitalAssetStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(digitalAssetStoreReducer, {
    digitalAsset: [],
    digitalAssetLoading: true,
    digitalAssetFilestoUpload: [],
    digitalAssetSavingNew: false,
  });

  const deleteDigitalAssetById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchDigitalAssetData = useV3GenericGetData<DigitalAsset[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createDigitalAsset = useV3Create<DigitalAsset>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateDigitalAsset = useV3Update<DigitalAsset>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<DigitalAsset>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleDigitalAssetFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.digitalAssetFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "digital-assets",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_DIGITAL_ASSET",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_DIGITAL_ASSET_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            slackHook(
              `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path}`
            );
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          slackHook(
            `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path} with message "${error}"`
          );
        }
      }
    },
    [state.digitalAssetFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadDigitalAssetDocument = useCallback(
    async (digitalAsset_id: string, document_id: string | null) => {
      if (!document_id || !digitalAsset_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "digital-assets",
        entity_id: digitalAsset_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in digitalAsset objects' documents array
            downloadFileFromS3(
              res,
              state.digitalAsset
                ?.find((p) => p.id === digitalAsset_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            slackHook(
              `Document Error: Failed to get document with message "No response"`
            );
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          slackHook(
            `Document Error: Failed to get document with message "${err}"`
          );
        });
    },
    [downloadDocument, state.digitalAsset, ud_id]
  );

  const handleDeleteDigitalAssetDocument = useCallback(
    async (
      digitalAsset_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !digitalAsset_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "digital-assets",
          entity_id: digitalAsset_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the digitalAsset object
          const updatedDigitalAsset = state.digitalAsset.map((p) => {
            if (p.id === digitalAsset_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_DIGITAL_ASSET",
            payload: updatedDigitalAsset,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.digitalAsset, deleteDocumentById, ud_id]
  );

  const handleDigitalAssetFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_DIGITAL_ASSET_FILES_TO_UPLOAD",
        payload: [...state.digitalAssetFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.digitalAssetFilestoUpload]
  );

  const handleDigitalAssetFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.digitalAssetFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_DIGITAL_ASSET_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.digitalAssetFilestoUpload]
  );

  const handleDigitalAssetFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_DIGITAL_ASSET_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchDigitalAssetItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchDigitalAssetData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "digital-assets",
        });

        if (isMounted()) {
          dispatch({ type: "SET_DIGITAL_ASSET", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchDigitalAssetData, isMounted]
  );

  const handleAddOrUpdateDigitalAssetSingleCall = useCallback(
    async (digitalAssetItem: DigitalAsset): Promise<DigitalAsset | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(digitalAssetItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateDigitalAsset(
            {
              ud_id: ud_id,
              entity: "digital-assets",
              entity_id: digitalAssetItem.id || undefined,
            },
            digitalAssetItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createDigitalAsset(
            { ud_id: ud_id, entity: "digital-assets" }, // Adjust according to your actual API parameter requirements
            digitalAssetItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_DIGITAL_ASSET", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save digitalAsset item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createDigitalAsset, updateDigitalAsset]
  );

  const handleAddOrUpdateDigitalAsset = useCallback(
    async (digitalAssetItem: DigitalAsset) => {
      dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: true });
      if (state.digitalAssetFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateDigitalAssetSingleCall(
            digitalAssetItem
          );
          if (res) {
            await handleDigitalAssetFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit digitalAsset data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateDigitalAssetSingleCall(digitalAssetItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit digitalAsset data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateDigitalAssetSingleCall,
      state.digitalAssetFilestoUpload,
      handleDigitalAssetFilesUpload,
    ]
  );

  const handleDeleteDigitalAsset = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteDigitalAssetById({
          ud_id: ud_id,
          entity: "digital-assets",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_DIGITAL_ASSET", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_DIGITAL_ASSET_SAVING_NEW", payload: false });
      }
    },
    [deleteDigitalAssetById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchDigitalAssetItems(ud_id); // Fetch all digitalAsset items
    }
  }, [ fetchDigitalAssetItems, ud_id]);

  return {
    digitalAsset: state.digitalAsset,
    digitalAssetLoading: state.digitalAssetLoading,
    digitalAssetSavingNew: state.digitalAssetSavingNew,
    digitalAssetFilestoUpload: state.digitalAssetFilestoUpload,
    handleAddOrUpdateDigitalAsset,
    handleDeleteDigitalAsset,
    handleDigitalAssetFileRemove,
    handleDigitalAssetFilesDrop,
    handleDigitalAssetFilesRemoveAll,
    handleDownloadDigitalAssetDocument,
    handleDeleteDigitalAssetDocument,
    handleDigitalAssetFilesUpload,
  };
};
